import { useRef, useState } from 'react';
import { roleLabel } from '@technohull/shared';
import type { Me } from '../auth/AuthContext.js';
import { useUpdateProfile } from '../features/hooks.js';
import { InlineError, Modal } from './ui.js';
import { Avatar } from './Avatar.js';

// Resize/crop an image file to a square data URL so avatars stay a few KB (stored in D1).
// uses a data: URL end-to-end (FileReader) so it works under the app's CSP (img-src 'self'
// data:), which blocks blob: URLs.
function resizeToDataUrl(file: File, size = 128): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('could not read file'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('canvas unavailable'));
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => reject(new Error('could not read image'));
      img.src = reader.result as string; // data: URL - allowed by CSP
    };
    reader.readAsDataURL(file);
  });
}

export function ProfileModal({ me, onClose }: { me: Me; onClose: () => void }) {
  const update = useUpdateProfile();
  const [name, setName] = useState(me.user.displayName);
  const [avatar, setAvatar] = useState<string | null | undefined>(me.user.avatar ?? null);
  const [imgError, setImgError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = async (file?: File) => {
    if (!file) return;
    setImgError('');
    try {
      setAvatar(await resizeToDataUrl(file));
    } catch {
      setImgError('Could not process that image.');
    }
  };

  const nameChanged = name.trim() && name.trim() !== me.user.displayName;
  const avatarChanged = (avatar ?? null) !== (me.user.avatar ?? null);
  const dirty = nameChanged || avatarChanged;

  const save = () => {
    update.mutate(
      {
        displayName: nameChanged ? name.trim() : undefined,
        avatar: avatarChanged ? avatar ?? null : undefined,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title="Your profile"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!dirty || update.isPending} onClick={save}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
        <Avatar name={name || me.user.email} avatar={avatar} size={72} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* a real button (a <label> around a hidden input can't take keyboard focus) */}
          <button type="button" className="btn btn-sm" onClick={() => fileRef.current?.click()}>Upload photo</button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onFile(e.target.files?.[0])} />
          {avatar && <button className="btn btn-sm btn-ghost" onClick={() => setAvatar(null)}>Remove photo</button>}
        </div>
      </div>
      {imgError && <InlineError error={new Error(imgError)} onDismiss={() => setImgError('')} />}

      <label className="field-label" htmlFor="profile-name">Display name</label>
      <input id="profile-name" className="field" style={{ width: '100%' }} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      <div className="muted" style={{ marginTop: 8, fontSize: 11 }}>{me.user.email} · {roleLabel(me.user.role)}</div>

      {update.isError && <InlineError error={update.error} onDismiss={() => update.reset()} />}
    </Modal>
  );
}
